import React, { useEffect, useMemo, useState } from "react";
import { consumeNavPath } from "@/lib/adminNav";
import { supabase } from "@/lib/supabase";
import { formatDate } from "@/lib/dates";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { BarChart3, CreditCard, Users, ChevronDown, ChevronRight, Layers, Tag as TagIcon } from "lucide-react";
import ECardTagReport from "@/components/ECardTagReport";


type TrendGroup = "day" | "week" | "month";

type ECardRow = {
  id: string;
  card_last4: string;
  purchase_date: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  status: string;
  sold_by_driver_name: string | null;
};

type EftposRow = {
  id: number;
  settlement_date: string | null;
  transaction_date: string | null;
  pan: string | null;
  logo: string | null;
  type: string | null;
  transaction_amount: number | null;
};

type Topup = {
  id: string;
  ecard_id: string;
  topup_date: string;
  topup_amount: number;
  bonus_amount: number;
  apply_amount: number;
};

type JoinedTxn = {
  id: number;
  settlement_date: string | null;
  transaction_date: string | null;
  last4: string;
  transaction_amount: number;
  logo: string | null;
  type: string | null;

  ecard_id: string | null;
  purchase_date: string | null;
  customer_name: string;
  card_status: string | null;
};

const isoDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(
    Number.isFinite(n) ? n : 0
  );

function last4FromPan(pan: string | null) {
  const p = (pan ?? "").trim();
  if (!p) return "----";
  return p.slice(-4);
}

// basic grouping key generators
function groupKey(dateIso: string, groupBy: TrendGroup) {
  // dateIso is YYYY-MM-DD
  if (groupBy === "day") return dateIso;
  if (groupBy === "month") return dateIso.slice(0, 7); // YYYY-MM

  // week: ISO-ish week grouping (good enough for reporting)
  // We'll group by Monday-start week using JS Date.
  const d = new Date(dateIso + "T00:00:00");
  const day = d.getDay(); // 0 Sun .. 6 Sat
  const diffToMon = (day === 0 ? -6 : 1) - day;
  d.setDate(d.getDate() + diffToMon);
  return isoDate(d); // week starting Monday
}

export default function AdminECardReportingPage() {
  const [activeTab, setActiveTab] = useState("eftpos");

  // Deep-link from the Site Map (Settings -> Site Map) -- see adminNav.ts.
  // path[0] ("reporting") is consumed by AdminECardHub.tsx; this reads path[1].
  useEffect(() => {
    const path = consumeNavPath();
    if (path?.[1]) setActiveTab(path[1]);
  }, []);

  // EFTPOS Tab State
  const [dateFrom, setDateFrom] = useState<string>(() =>
    isoDate(new Date(Date.now() - 30 * 86400000))
  );
  const [dateTo, setDateTo] = useState<string>(() => isoDate(new Date()));
  const [loading, setLoading] = useState(false);

  const [cards, setCards] = useState<ECardRow[]>([]);
  const [txns, setTxns] = useState<JoinedTxn[]>([]);

  const [search, setSearch] = useState("");
  const [cardFilter, setCardFilter] = useState<string>("all"); // last4 or 'all'
  const [groupBy, setGroupBy] = useState<TrendGroup>("week");

  // Summary Tab State
  const [allTopups, setAllTopups] = useState<Topup[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  
  // Collapsible states for summary sections
  const [driverSectionOpen, setDriverSectionOpen] = useState(false);
  const [topupCountSectionOpen, setTopupCountSectionOpen] = useState(false);
  const [topupLevelSectionOpen, setTopupLevelSectionOpen] = useState(false);

  async function loadCards() {
    const res = await supabase
      .from("ecards")
      .select("id, card_last4, purchase_date, customer_first_name, customer_last_name, status, sold_by_driver_name")
      .order("created_at", { ascending: false });

    if (res.error) {
      toast({ title: "Failed to load eCards", description: res.error.message });
      return;
    }
    setCards(Array.isArray(res.data) ? (res.data as ECardRow[]) : []);
  }

  async function loadTopups() {
    const res = await supabase
      .from("ecard_topups")
      .select("id, ecard_id, topup_date, topup_amount, bonus_amount, apply_amount")
      .order("topup_date", { ascending: true });

    if (res.error) {
      toast({ title: "Failed to load topups", description: res.error.message });
      return;
    }
    setAllTopups(Array.isArray(res.data) ? (res.data as Topup[]) : []);
  }

  async function runReport() {
    if (!dateFrom || !dateTo) {
      toast({ title: "Pick both dates", description: "From + To are required." });
      return;
    }

    setLoading(true);
    try {
      // Ensure we have cards loaded (for join)
      if (cards.length === 0) await loadCards();

      const res = await supabase
        .from("eftpos_data")
        .select("id, settlement_date, transaction_date, pan, logo, type, transaction_amount")
        .ilike("logo", "%ECARD%")
        .gte("settlement_date", dateFrom)
        .lte("settlement_date", dateTo)
        .order("settlement_date", { ascending: false });

      if (res.error) throw res.error;

      const rows = Array.isArray(res.data) ? (res.data as EftposRow[]) : [];

      // Build lookup map: last4 -> best matching ecard row
      // (If you ever have collisions, we can improve this by linking to full card_number later.)
      const byLast4 = new Map<string, ECardRow>();
      for (const c of cards) {
        if (c.card_last4) byLast4.set(c.card_last4, c);
      }

      const joined: JoinedTxn[] = rows.map((r) => {
        const last4 = last4FromPan(r.pan);
        const match = byLast4.get(last4) ?? null;

        const customer_name = match
          ? `${match.customer_first_name ?? ""} ${match.customer_last_name ?? ""}`.trim()
          : "";

        return {
          id: r.id,
          settlement_date: r.settlement_date,
          transaction_date: r.transaction_date,
          last4,
          transaction_amount: Number(r.transaction_amount ?? 0),
          logo: r.logo ?? null,
          type: r.type ?? null,

          ecard_id: match?.id ?? null,
          purchase_date: match?.purchase_date ?? null,
          customer_name: customer_name || "(no name)",
          card_status: match?.status ?? null,
        };
      });

      setTxns(joined);
      toast({
        title: "Report loaded",
        description: `${joined.length} eCard transactions found for ${dateFrom} → ${dateTo}`,
      });
    } catch (e: any) {
      console.error(e);
      toast({ title: "Report failed", description: e?.message ?? "Unknown error" });
    } finally {
      setLoading(false);
    }
  }

  async function loadSummaryData() {
    setSummaryLoading(true);
    try {
      await Promise.all([loadCards(), loadTopups()]);
      toast({ title: "Summary data loaded" });
    } catch (e: any) {
      toast({ title: "Failed to load summary", description: e?.message ?? "Unknown error" });
    } finally {
      setSummaryLoading(false);
    }
  }

  useEffect(() => {
    void loadCards();
  }, []);

  // Load summary data when switching to summary tab
  useEffect(() => {
    if (activeTab === "summary" && allTopups.length === 0) {
      void loadSummaryData();
    }
  }, [activeTab]);

  const filteredTxns = useMemo(() => {
    const q = search.trim().toLowerCase();

    return txns.filter((t) => {
      if (cardFilter !== "all" && t.last4 !== cardFilter) return false;

      if (!q) return true;

      const hay = [
        t.last4,
        t.customer_name,
        t.purchase_date ?? "",
        t.card_status ?? "",
        t.type ?? "",
        t.logo ?? "",
      ]
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [txns, search, cardFilter]);

  const kpis = useMemo(() => {
    const totalSpend = filteredTxns.reduce((sum, t) => sum + (t.transaction_amount || 0), 0);
    const count = filteredTxns.length;
    const uniqueCards = new Set(filteredTxns.map((t) => t.last4)).size;

    const unmatched = filteredTxns.filter((t) => !t.ecard_id).length;

    return { totalSpend, count, uniqueCards, unmatched };
  }, [filteredTxns]);

  const trend = useMemo(() => {
    // group by settlement_date (date only)
    const buckets: Record<string, { total: number; count: number }> = {};

    for (const t of filteredTxns) {
      const d = t.settlement_date ?? "";
      if (!d) continue;

      const key = groupKey(d, groupBy);
      if (!buckets[key]) buckets[key] = { total: 0, count: 0 };
      buckets[key].total += t.transaction_amount || 0;
      buckets[key].count += 1;
    }

    const rows = Object.entries(buckets)
      .map(([k, v]) => ({ bucket: k, total: v.total, count: v.count }))
      .sort((a, b) => (a.bucket < b.bucket ? -1 : 1));

    return rows;
  }, [filteredTxns, groupBy]);

  // Card list for dropdown: only cards that appear in filtered range OR all cards?
  const cardOptions = useMemo(() => {
    // show all known card last4s (from ecards) + those from txns that didn't match
    const set = new Set<string>();
    for (const c of cards) if (c.card_last4) set.add(c.card_last4);
    for (const t of txns) if (t.last4) set.add(t.last4);

    return Array.from(set).sort();
  }, [cards, txns]);

  // ========== Summary Tab Calculations ==========
  
  // Cards sold by driver
  const cardsByDriver = useMemo(() => {
    const driverCounts: Record<string, number> = {};
    
    for (const card of cards) {
      const driver = card.sold_by_driver_name || "Unassigned";
      driverCounts[driver] = (driverCounts[driver] || 0) + 1;
    }
    
    return Object.entries(driverCounts)
      .map(([driver, count]) => ({ driver, count }))
      .sort((a, b) => b.count - a.count);
  }, [cards]);

  // Group topups by card to determine topup number (1st, 2nd, 3rd, etc)
  const topupsByCard = useMemo(() => {
    const grouped: Record<string, Topup[]> = {};
    
    for (const topup of allTopups) {
      if (!grouped[topup.ecard_id]) {
        grouped[topup.ecard_id] = [];
      }
      grouped[topup.ecard_id].push(topup);
    }
    
    // Sort each card's topups by date
    for (const cardId of Object.keys(grouped)) {
      grouped[cardId].sort((a, b) => 
        new Date(a.topup_date).getTime() - new Date(b.topup_date).getTime()
      );
    }
    
    return grouped;
  }, [allTopups]);

  // Distribution of cards by number of topups
  const topupDistribution = useMemo(() => {
    const distribution: Record<number, number> = {};
    
    for (const cardId of Object.keys(topupsByCard)) {
      const count = topupsByCard[cardId].length;
      distribution[count] = (distribution[count] || 0) + 1;
    }
    
    // Also count cards with 0 topups
    const cardsWithTopups = new Set(Object.keys(topupsByCard));
    const cardsWithoutTopups = cards.filter(c => !cardsWithTopups.has(c.id)).length;
    if (cardsWithoutTopups > 0) {
      distribution[0] = cardsWithoutTopups;
    }
    
    return Object.entries(distribution)
      .map(([count, cards]) => ({ topupCount: parseInt(count), cardCount: cards }))
      .sort((a, b) => a.topupCount - b.topupCount);
  }, [topupsByCard, cards]);

  // Totals by topup number (1st topup, 2nd topup, etc)
  const topupLevelTotals = useMemo(() => {
    const levels: Record<number, { totalPaid: number; totalBonus: number; count: number }> = {};
    
    for (const cardId of Object.keys(topupsByCard)) {
      const cardTopups = topupsByCard[cardId];
      cardTopups.forEach((topup, index) => {
        const level = index + 1; // 1st, 2nd, 3rd, etc
        if (!levels[level]) {
          levels[level] = { totalPaid: 0, totalBonus: 0, count: 0 };
        }
        levels[level].totalPaid += topup.topup_amount;
        levels[level].totalBonus += topup.bonus_amount;
        levels[level].count += 1;
      });
    }
    
    return Object.entries(levels)
      .map(([level, data]) => ({ 
        level: parseInt(level), 
        totalPaid: data.totalPaid, 
        totalBonus: data.totalBonus,
        count: data.count
      }))
      .sort((a, b) => a.level - b.level);
  }, [topupsByCard]);

  // Overall totals
  const overallTotals = useMemo(() => {
    const totalPaid = allTopups.reduce((sum, t) => sum + t.topup_amount, 0);
    const totalBonus = allTopups.reduce((sum, t) => sum + t.bonus_amount, 0);
    return { totalPaid, totalBonus, totalTopups: allTopups.length };
  }, [allTopups]);

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-2xl">
          <TabsTrigger value="eftpos" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            EFTPOS Transactions
          </TabsTrigger>
          <TabsTrigger value="summary" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Sales & Top-ups
          </TabsTrigger>
          <TabsTrigger value="tags" className="flex items-center gap-2">
            <TagIcon className="h-4 w-4" />
            Tags
          </TabsTrigger>
        </TabsList>


        {/* EFTPOS Transactions Tab */}
        <TabsContent value="eftpos" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>eCard Reporting (EFTPOS • logo contains "ECARD")</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label>From Date</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>

                <div>
                  <Label>To Date</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>

                <div>
                  <Label>Card (last 4)</Label>
                  <Select value={cardFilter} onValueChange={setCardFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All cards</SelectItem>
                      {cardOptions.map((l4) => (
                        <SelectItem key={l4} value={l4}>
                          {l4}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Search</Label>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="last4, name, status…"
                  />
                </div>

                <div className="flex items-end">
                  <Button onClick={runReport} disabled={loading} className="w-full">
                    {loading ? "Loading..." : "Run Report"}
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">Spend: {fmtMoney(kpis.totalSpend)}</Badge>
                <Badge variant="secondary">Transactions: {kpis.count}</Badge>
                <Badge variant="outline">Unique cards: {kpis.uniqueCards}</Badge>
                {kpis.unmatched > 0 && (
                  <Badge variant="destructive">Unmatched last4: {kpis.unmatched}</Badge>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Trend</CardTitle>
                      <Select value={groupBy} onValueChange={(v) => setGroupBy(v as TrendGroup)}>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="day">Daily</SelectItem>
                          <SelectItem value="week">Weekly</SelectItem>
                          <SelectItem value="month">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{groupBy === "week" ? "Week starting" : groupBy}</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {trend.map((r) => (
                          <TableRow key={r.bucket}>
                            <TableCell className="font-medium">{r.bucket}</TableCell>
                            <TableCell className="text-right">{fmtMoney(r.total)}</TableCell>
                            <TableCell className="text-right">{r.count}</TableCell>
                          </TableRow>
                        ))}
                        {trend.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} className="text-sm text-muted-foreground">
                              Run report to see trend.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Card snapshot (from ecards table)</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    {cardFilter === "all" ? (
                      <div className="text-muted-foreground">
                        Select a specific card (last 4) to see purchase date + customer details here.
                      </div>
                    ) : (
                      (() => {
                        const c = cards.find((x) => x.card_last4 === cardFilter);
                        if (!c)
                          return (
                            <div className="text-muted-foreground">
                              No matching eCard record found for last4 {cardFilter}.
                            </div>
                          );

                        const name = `${c.customer_first_name ?? ""} ${c.customer_last_name ?? ""}`.trim();

                        return (
                          <div className="space-y-2">
                            <div className="flex flex-wrap gap-2">
                              <Badge variant="outline">Last4: {c.card_last4}</Badge>
                              <Badge variant="outline">Status: {c.status}</Badge>
                              {c.purchase_date && <Badge variant="secondary">Purchased: {formatDate(c.purchase_date)}</Badge>}
                            </div>
                            <div>
                              <div className="font-medium">{name || "(no name)"}</div>
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>

          {/* Transaction list */}
          <Card>
            <CardHeader>
              <CardTitle>Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Settlement</TableHead>
                      <TableHead>Card</TableHead>
                      <TableHead>Purchase Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTxns.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell className="text-sm">{t.settlement_date ?? "—"}</TableCell>
                        <TableCell className="text-sm font-medium">{t.last4}</TableCell>
                        <TableCell className="text-sm">{t.purchase_date ?? "—"}</TableCell>
                        <TableCell className="text-sm">{t.customer_name}</TableCell>
                        <TableCell className="text-sm text-right">{fmtMoney(t.transaction_amount)}</TableCell>
                        <TableCell className="text-sm">{t.type ?? "—"}</TableCell>
                        <TableCell className="text-sm">
                          {t.card_status ? <Badge variant="outline">{t.card_status}</Badge> : <Badge variant="secondary">Unmatched</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}

                    {filteredTxns.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-sm text-muted-foreground">
                          No transactions. Run report, widen date range, or clear filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="text-xs text-muted-foreground mt-2">
                Matching is by PAN last4 ↔ ecard.card_last4. If you ever get last4 collisions, we can hard-link eftpos rows to ecards later (stored mapping table).
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales & Top-ups Summary Tab */}
        <TabsContent value="summary" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Card Sales & Top-ups Summary</h2>
            <Button onClick={loadSummaryData} disabled={summaryLoading}>
              {summaryLoading ? "Loading..." : "Refresh Data"}
            </Button>
          </div>

          {/* Overall Totals */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Cards</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{cards.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Overall Paid Amount</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{fmtMoney(overallTotals.totalPaid)}</div>
                <div className="text-sm text-muted-foreground">{overallTotals.totalTopups} top-ups</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Overall Bonus Amount</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{fmtMoney(overallTotals.totalBonus)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Cards Sold by Driver - Collapsible */}
          <Collapsible open={driverSectionOpen} onOpenChange={setDriverSectionOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Cards Sold by Driver
                      <Badge variant="secondary" className="ml-2">{cardsByDriver.length} drivers</Badge>
                    </div>
                    {driverSectionOpen ? (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Driver</TableHead>
                          <TableHead className="text-right">Cards Sold</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cardsByDriver.map((row) => (
                          <TableRow key={row.driver}>
                            <TableCell className="font-medium">{row.driver}</TableCell>
                            <TableCell className="text-right">{row.count}</TableCell>
                          </TableRow>
                        ))}
                        {cardsByDriver.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} className="text-sm text-muted-foreground">
                              No data available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Cards by Number of Top-ups - Collapsible */}
          <Collapsible open={topupCountSectionOpen} onOpenChange={setTopupCountSectionOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Cards by Number of Top-ups
                      <Badge variant="secondary" className="ml-2">{topupDistribution.length} levels</Badge>
                    </div>
                    {topupCountSectionOpen ? (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Top-up Count</TableHead>
                          <TableHead className="text-right">Number of Cards</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {topupDistribution.map((row) => (
                          <TableRow key={row.topupCount}>
                            <TableCell className="font-medium">
                              {row.topupCount === 0 ? "No top-ups" : `${row.topupCount} top-up${row.topupCount > 1 ? "s" : ""}`}
                            </TableCell>
                            <TableCell className="text-right">{row.cardCount}</TableCell>
                          </TableRow>
                        ))}
                        {topupDistribution.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} className="text-sm text-muted-foreground">
                              No data available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Totals by Top-up Level - Collapsible */}
          <Collapsible open={topupLevelSectionOpen} onOpenChange={setTopupLevelSectionOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Layers className="h-5 w-5" />
                      Totals by Top-up Level (1st, 2nd, 3rd, etc.)
                      <Badge variant="secondary" className="ml-2">{topupLevelTotals.length} levels</Badge>
                    </div>
                    {topupLevelSectionOpen ? (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Top-up Level</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                          <TableHead className="text-right">Total Paid Amount</TableHead>
                          <TableHead className="text-right">Total Bonus Amount</TableHead>
                          <TableHead className="text-right">Avg Paid</TableHead>
                          <TableHead className="text-right">Avg Bonus</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {topupLevelTotals.map((row) => (
                          <TableRow key={row.level}>
                            <TableCell className="font-medium">
                              {row.level === 1 ? "1st" : row.level === 2 ? "2nd" : row.level === 3 ? "3rd" : `${row.level}th`} Top-up
                            </TableCell>
                            <TableCell className="text-right">{row.count}</TableCell>
                            <TableCell className="text-right text-blue-600 font-medium">
                              {fmtMoney(row.totalPaid)}
                            </TableCell>
                            <TableCell className="text-right text-green-600 font-medium">
                              {fmtMoney(row.totalBonus)}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {fmtMoney(row.count > 0 ? row.totalPaid / row.count : 0)}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {fmtMoney(row.count > 0 ? row.totalBonus / row.count : 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {topupLevelTotals.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-sm text-muted-foreground">
                              No top-up data available.
                            </TableCell>
                          </TableRow>
                        )}
                        {/* Totals Row */}
                        {topupLevelTotals.length > 0 && (
                          <TableRow className="bg-muted/50 font-bold">
                            <TableCell>TOTAL</TableCell>
                            <TableCell className="text-right">{overallTotals.totalTopups}</TableCell>
                            <TableCell className="text-right text-blue-600">
                              {fmtMoney(overallTotals.totalPaid)}
                            </TableCell>
                            <TableCell className="text-right text-green-600">
                              {fmtMoney(overallTotals.totalBonus)}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {fmtMoney(overallTotals.totalTopups > 0 ? overallTotals.totalPaid / overallTotals.totalTopups : 0)}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {fmtMoney(overallTotals.totalTopups > 0 ? overallTotals.totalBonus / overallTotals.totalTopups : 0)}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </TabsContent>

        {/* Tag Reporting Tab */}
        <TabsContent value="tags" className="space-y-6">
          <ECardTagReport />
        </TabsContent>
      </Tabs>

    </div>
  );
}
